// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { generateEndpointInstallationKey, getEndpointInstallerData } from '@api/portalServices';
import type { EndpointInstallerData } from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery } from '@tanstack/react-query';

type InstallationKey = {
  key: string; // decrypted plaintext, held in memory only — never log (REQ-033)
  expirationDate: string;
};

/**
 * Backs the agent-settings drawer. Installer data (version + installer URL)
 * loads when the drawer opens. The installation key is generated lazily via a
 * mutation the user triggers with the "Generate key" button — it is NOT fetched
 * on open. The key arrives encrypted and is decrypted in-memory with the same
 * per-request coding context that signed the generate call (REQ-033).
 */
export const useAgentInstaller = (enabled: boolean) => {
  const { tenantId } = useSession();
  const isEnabled = enabled && Boolean(tenantId);

  const installerData = useQuery<EndpointInstallerData, ApiError>({
    queryKey: [QueryKey.ENDPOINT_INSTALLER_DATA, tenantId],
    queryFn: getEndpointInstallerData,
    enabled: isEnabled,
    retry: false,
  });

  const installationKey = useMutation<InstallationKey, ApiError>({
    mutationFn: async () => {
      const ctx = await createCodingCtxV2();
      const res = await generateEndpointInstallationKey(ctx);
      return { key: ctx.decryptToString(res.installationKey), expirationDate: res.expirationDate };
    },
    retry: false,
  });

  return { installerData, installationKey };
};
